import { useSelector } from "react-redux";
import { isViewerReady } from "../../../api/features/viewer/selectors";
import useUser from "../../user/useUser";
import { useEffect, useState } from "react";
import {
  combineDetailedStatuses,
  DetailedLoadStatus,
} from "../../../../../../common/utils/async-status";
import {
  applyContentControlSettingsInBulk,
  EnhancedContentControls,
} from "../../../api/features/content-controls/enhanced/actions";
import useBookInfo from "../../bookinfo/useBookInfo";
import { getLabEnabledStatuses } from "../../../api/features/user/selectors";
import { useBrand } from "../../../../../../common/providers/brand/brand-context";
import useReaderView from "./useReaderView";
import { ReaderView } from "../../../api/features/content-controls/enhanced/utils";
import usePaginationSupport from "./usePaginationSupport";
import { scrollToStartLocation } from "../../../api/features/hash-nav/actions";
import { useAppDispatch } from "../../../api/core/useAppDispatch";

export default function useApplySettings() {
  const viewerReady = useSelector(isViewerReady);
  const { loadStatus: userLoadStatus } = useUser();
  const { loadStatus: bookInfoLoadStatus } = useBookInfo();
  const dispatch = useAppDispatch();
  const labStatuses = useSelector(getLabEnabledStatuses);
  const brand = useBrand();
  const { currentReaderView } = useReaderView();
  const [overrides, setOverrides] = useState<EnhancedContentControls>();
  const { paginationActive } = usePaginationSupport();

  const loadStatus = combineDetailedStatuses([
    userLoadStatus,
    bookInfoLoadStatus,
  ]);

  useEffect(() => {
    if (
      loadStatus === DetailedLoadStatus.OK &&
      currentReaderView &&
      !overrides
    ) {
      // a hook to override stored settings before they are rendered in mosaic
      if (!paginationActive && currentReaderView.value !== ReaderView.DEFAULT) {
        // if pagination beta was turned off in another window, flip
        // back to default here, as pagination preferences are no longer available
        // can also happen when a brand turns off labs
        setOverrides({ readerView: ReaderView.DEFAULT });
      } else {
        // the empty object signals overrides have been considered but not applicable
        setOverrides({});
      }
    }
  }, [
    brand,
    currentReaderView,
    labStatuses,
    loadStatus,
    overrides,
    paginationActive,
  ]);

  useEffect(() => {
    if (viewerReady && loadStatus === DetailedLoadStatus.OK && overrides) {
      dispatch(applyContentControlSettingsInBulk(false, overrides)).then(() => {
        dispatch(scrollToStartLocation());
      });
    }
  }, [dispatch, loadStatus, overrides, viewerReady]);
}
